import React from "react";

import { isAdmin, isSuperAdmin, isVisitor } from "../../../utils/Permissions";
import ApplicantNameCell from "./components/ApplicantNameCell";
import ApplicantTagCell from "./components/ApplicantTagCell";
import {
  applicantsTextColumn,
  renderApplicantsTruncatedCell,
} from "./applicantsListColumnHelpers";
import { UIRating, UIRowActionsMenu } from "../../../components/ui";

export const getApplicantsListColumns = ({
  applicants,
  shared,
  canEditTags,
  canAdd,
  onAddTag,
  onTagClick,
  onEditProfile,
}) => [
  applicantsTextColumn({
    field: "id",
    headerName: shared.id,
    editable: false,
    filterable: false,
    hideable: false,
    hideSortIcons: true,
    sortable: false,
    flex: 0,
    width: 150,
    minWidth: 150,
    cellClassName: "applicants-page-container__id-cell",
    renderCell: (params) => (
      <div className="MuiDataGrid-cellContent applicants-page-container__cell-content applicants-page-container__cell-content--id">
        {params.row?.id ? <span>#{params.row.id}</span> : "-"}
      </div>
    ),
  }),
  applicantsTextColumn({
    field: "name",
    headerName: applicants.listApplicants.name,
    editable: false,
    filterable: false,
    hideable: false,
    hideSortIcons: true,
    sortable: false,
    flex: 1.35,
    minWidth: 220,
    renderCell: (params) => <ApplicantNameCell row={params.row} />,
  }),
  applicantsTextColumn({
    field: "email",
    headerName: applicants.listApplicants.email,
    editable: false,
    filterable: false,
    hideable: false,
    hideSortIcons: true,
    sortable: false,
    flex: 1.5,
    minWidth: 260,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "role",
    headerName: applicants.listApplicants.role,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "tags",
    headerName: applicants.listApplicants.tags,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    flex: 1.1,
    minWidth: 220,
    cellClassName: "applicants-page-container__tags-cell",
    renderCell: (params) => (
      <div className="applicants-page-container__tags-cell-content">
        <ApplicantTagCell
          row={params.row}
          addTagLabel={applicants.listApplicants.addTag}
          canEditTags={canEditTags}
          onTagClick={onTagClick}
        />
      </div>
    ),
  }),
  applicantsTextColumn({
    field: "nationality",
    headerName: applicants.listApplicants.nationality,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "city",
    headerName: applicants.listApplicants.cityOfResidence,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    flex: 1,
    minWidth: 160,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "fieldsOfStudy",
    headerName: applicants.listApplicants.studyField,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    flex: 0.65,
    minWidth: 100,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  {
    field: "profileRate",
    headerName: applicants.listApplicants.profileRate,
    editable: false,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: ({ value }) => (
      <UIRating value={value ?? 0} variant="compact" />
    ),
  },
  applicantsTextColumn({
    field: "age",
    headerName: applicants.listApplicants.age,
    hide: true,
    editable: false,
    filterable: false,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "language",
    headerName: applicants.listApplicants.language,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: (params) => {
      const text = params.row.language.length
        ? params.row.language.map((item) => item.language).join(", ")
        : "-";
      return renderApplicantsTruncatedCell(text);
    },
  }),
  applicantsTextColumn({
    field: "gender",
    headerName: applicants.listApplicants.gender,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "weight",
    headerName: applicants.listApplicants.weight,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "height",
    headerName: applicants.listApplicants.height,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "profileStatus",
    headerName: applicants.listApplicants.profileStatus,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "applicantStatus",
    headerName: applicants.listApplicants.applicantStatus,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: ({ value }) => renderApplicantsTruncatedCell(value),
  }),
  applicantsTextColumn({
    field: "disability",
    headerName: applicants.listApplicants.disability,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: (params) => {
      const text = params.row.disability.data.length
        ? params.row.disability.data.map((item) => item.name).join(", ")
        : params.row.disability.status === "verified"
          ? "-"
          : applicants.listApplicants.noDisability;
      return renderApplicantsTruncatedCell(text);
    },
  }),
  applicantsTextColumn({
    field: "diseases",
    headerName: applicants.listApplicants.diseases,
    editable: false,
    hide: true,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    renderCell: (params) => {
      const text = params.row.diseases.data.length
        ? params.row.diseases.data.map((item) => item.name).join(", ")
        : params.row.diseases.status === "verified"
          ? "-"
          : applicants.listApplicants.noDisease;
      return renderApplicantsTruncatedCell(text);
    },
  }),
  {
    field: "Actions",
    headerName: applicants.applicantRequsets.actions,
    editable: false,
    width: 90,
    minWidth: 80,
    flex: 0,
    filterable: false,
    hideable: true,
    hideSortIcons: true,
    sortable: false,
    align: "center",
    headerAlign: "center",
    cellClassName: "applicants-page-container__actions-cell",
    renderCell: (params) => {
      const items = [];
      const stopRowClick = { stopPropagation: () => {} };
      const canAddTag =
        !params.row.userTag &&
        ((isVisitor() && canAdd) || isAdmin() || isSuperAdmin());

      if (canAddTag) {
        items.push({
          key: "addTag",
          label: applicants.listApplicants.addTag,
          onClick: () => onAddTag(params.row, stopRowClick),
        });
      }

      if (isSuperAdmin()) {
        items.push({
          key: "editProfile",
          label: shared.editProfile,
          onClick: () => onEditProfile(params.row.id, stopRowClick),
        });
      }

      return (
        <UIRowActionsMenu
          ariaLabel={applicants.applicantRequsets.actions}
          items={items}
        />
      );
    },
  },
];
